import { Category } from '@app/core/services/api/API.service';
import {
    CategoryAPI,
    CategoryNode,
    Image
} from '@app/shared/services/serverless/brandfactory-serverless.service';

/** Categorías visibles en el catálogo MC público (invitados). */
export function filterMcGuestCategories(categories: Category[]): Category[] {
    return (categories ?? []).filter(
        category => category?.status !== 'discontinued'
    );
}

function mapCategoryImage(category: Category): Image | undefined {
    const key = category.image?.key?.trim();
    if (!key) {
        return undefined;
    }
    return { key };
}

function readCategoryProductsCount(category: Category): number | undefined {
    const count = (category as CategoryAPI).productsCount;
    return typeof count === 'number' && Number.isFinite(count)
        ? count
        : undefined;
}

export function buildCategoryProductsCountMap(
    categories: Category[]
): Map<string, number> {
    const map = new Map<string, number>();

    for (const category of categories ?? []) {
        const count = readCategoryProductsCount(category);
        if (category?.id && count !== undefined) {
            map.set(category.id, count);
        }
    }

    return map;
}

/** Aplana un árbol cascade en lista con parentId para buildMcCategoryTree. */
export function flattenCategoryNodes(
    nodes: CategoryNode[],
    parentId?: string | null
): Category[] {
    const result: Category[] = [];

    for (const node of nodes ?? []) {
        if (!node?.id) {
            continue;
        }

        result.push({
            id: node.id,
            name: node.name,
            path: node.path,
            parentId: parentId ?? undefined,
            image: node.image,
            status: 'active'
        } as Category);

        if (node.children?.length) {
            result.push(...flattenCategoryNodes(node.children, node.id));
        }
    }

    return result;
}

function resolveNodeProductsCount(
    node: CategoryNode,
    countMap: Map<string, number>
): number {
    if (typeof node.productsCount === 'number') {
        return node.productsCount;
    }
    return countMap.get(node.id) ?? 0;
}

function categoryTreeHasProductsCountData(
    nodes: CategoryNode[],
    countMap: Map<string, number>
): boolean {
    if (countMap.size > 0) {
        return true;
    }

    for (const node of nodes ?? []) {
        if (typeof node.productsCount === 'number') {
            return true;
        }
        if (categoryTreeHasProductsCountData(node.children ?? [], countMap)) {
            return true;
        }
    }

    return false;
}

/**
 * Elimina categorías sin productos propios ni en descendientes.
 * Si no hay datos de conteo disponibles, devuelve el árbol sin cambios.
 */
export function filterCategoryTreeWithProducts(
    tree: CategoryNode[],
    categories: Category[] = []
): CategoryNode[] {
    const countMap = buildCategoryProductsCountMap(categories);
    if (!categoryTreeHasProductsCountData(tree, countMap)) {
        return tree;
    }

    const filterNode = (node: CategoryNode): CategoryNode | null => {
        const filteredChildren = (node.children ?? [])
            .map(child => filterNode(child))
            .filter((child): child is CategoryNode => child !== null);

        const ownCount = resolveNodeProductsCount(node, countMap);
        if (ownCount <= 0 && filteredChildren.length === 0) {
            return null;
        }

        return {
            ...node,
            children: filteredChildren
        };
    };

    return (tree ?? [])
        .map(node => filterNode(node))
        .filter((node): node is CategoryNode => node !== null);
}

export function buildMcCategoryTree(categories: Category[]): CategoryNode[] {
    const visible = filterMcGuestCategories(categories);
    if (!visible.length) {
        return [];
    }

    const nodeById = new Map<string, CategoryNode>();

    for (const category of visible) {
        if (!category?.id) {
            continue;
        }
        const image = mapCategoryImage(category);
        const productsCount = readCategoryProductsCount(category);
        nodeById.set(category.id, {
            id: category.id,
            name: category.name,
            path: category.path,
            children: [],
            ...(image ? { image } : {}),
            ...(productsCount !== undefined ? { productsCount } : {})
        });
    }

    const roots: CategoryNode[] = [];

    for (const category of visible) {
        const node = nodeById.get(category.id);
        if (!node) {
            continue;
        }

        const parentId = category.parentId?.trim();
        const parentNode = parentId ? nodeById.get(parentId) : undefined;

        if (parentNode) {
            parentNode.children.push(node);
        } else {
            roots.push(node);
        }
    }

    return sortCategoryNodes(roots);
}

function sortCategoryNodes(nodes: CategoryNode[]): CategoryNode[] {
    return [...nodes]
        .sort((a, b) => a.name.localeCompare(b.name, 'es'))
        .map(node => ({
            ...node,
            children: sortCategoryNodes(node.children ?? [])
        }));
}
